import React from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  AnnularVelocityInputs,
  JobLogEntry,
  StepProps,
  calcAV,
  calcEquivalentHoleSize,
  calcGPM
} from '../types/types';

const AVResultCard: React.FC<{
  label: string;
  value: number | null;
  unit?: string;
}> = ({ label, value, unit = 'ft/min' }) => (
  <div className="pjr-av-result">
    <div className="result-label">{label}</div>
    <div className="result-value">
      {value !== null ? value.toFixed(1) : '—'}
    </div>
    <div className="result-unit">{unit}</div>
  </div>
);

const Step5EngineeringLog: React.FC<StepProps> = ({ reportData, onChange }) => {
  const av = reportData.annularVelocity;

  const setAV = (field: keyof AnnularVelocityInputs, value: any) => {
    onChange({ annularVelocity: { ...av, [field]: value } });
  };

  const avNum = (
    field: keyof AnnularVelocityInputs,
    label: string,
    placeholder = '0',
    step = 'any'
  ) => (
    <Col md={2}>
      <Form.Group>
        <Form.Label className="fs-9 fw-semibold mb-1">{label}</Form.Label>
        <Form.Control
          size="sm"
          type="number"
          step={step}
          placeholder={placeholder}
          value={av[field]}
          onChange={e =>
            setAV(field, e.target.value === '' ? '' : Number(e.target.value))
          }
        />
      </Form.Group>
    </Col>
  );

  // ── Calculations ──────────────────────────────────────────────────────────────
  const gpm = calcGPM(av.pumpOutput, av.totalSPM);
  const eqHoleSize = calcEquivalentHoleSize(
    reportData.openingDia,
    reportData.pilotHoleDia
  );
  const avDPOpenHole = calcAV(gpm, av.idOfHole, av.odOfDP);
  const avDCOpenHole = calcAV(gpm, av.idOfHole, av.odOfCollars);
  const avDPCasing = calcAV(gpm, av.idOfCasing, av.odOfDP);
  const avDCCasing = calcAV(gpm, av.idOfCasing, av.odOfCollars);

  // ── Daily Log helpers ─────────────────────────────────────────────────────────
  const addLogEntry = () => {
    const today = new Date().toISOString().split('T')[0];
    onChange({
      jobLog: [...reportData.jobLog, { date: today, narrative: '' }]
    });
  };

  const removeLogEntry = (index: number) => {
    onChange({ jobLog: reportData.jobLog.filter((_, i) => i !== index) });
  };

  const updateLog = (
    index: number,
    field: keyof JobLogEntry,
    value: string
  ) => {
    const updated = reportData.jobLog.map((e, i) =>
      i === index ? { ...e, [field]: value } : e
    );
    onChange({ jobLog: updated });
  };

  return (
    <div>
      {/* ── Annular Velocity ──────────────────────────────────────────────────── */}
      <Card className="pjr-section-card mb-3">
        <Card.Header className="py-2 fw-semibold fs-9 text-uppercase">
          Equivalent Hole Size & Annular Velocity
        </Card.Header>
        <Card.Body className="py-3">
          <Row className="g-3 mb-3">
            {avNum('pumpOutput', 'Pump Output (bbl/stk)', '0.117', '0.001')}
            {avNum('totalSPM', 'Total SPM', '200', '1')}
            {avNum('idOfHole', 'Open Hole ID (in)', '18', '0.01')}
            {avNum('idOfCasing', 'Casing ID (in)', '20', '0.01')}
            {avNum('odOfCollars', 'OD of Collars (in)', '8', '0.01')}
            {avNum('odOfDP', 'OD of Drill Pipe (in)', '5.78', '0.01')}
          </Row>

          {/* GPM Display */}
          <Row className="g-2 mb-3 align-items-center">
            <Col md={3}>
              <div className="d-flex align-items-center gap-2">
                <span className="fs-9 fw-semibold text-muted">
                  Calculated GPM:
                </span>
                <span className="fw-bold text-primary fs-8">
                  {gpm !== null ? gpm.toFixed(0) : '—'} gpm
                </span>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-center gap-2">
                <span className="fs-9 fw-semibold text-muted">
                  Equivalent Hole Size:
                </span>
                <span className="fw-bold text-success fs-8">
                  {eqHoleSize !== null ? eqHoleSize.toFixed(3) : '—'} in
                </span>
                {reportData.openingDia !== '' &&
                  reportData.pilotHoleDia !== '' && (
                    <span className="text-muted fs-10">
                      (√{reportData.openingDia}² − {reportData.pilotHoleDia}²)
                    </span>
                  )}
              </div>
            </Col>
          </Row>

          {/* AV Results Grid */}
          <Row className="g-3">
            <Col md={3}>
              <AVResultCard label="AV — DP × Open Hole" value={avDPOpenHole} />
            </Col>
            <Col md={3}>
              <AVResultCard label="AV — DC × Open Hole" value={avDCOpenHole} />
            </Col>
            <Col md={3}>
              <AVResultCard label="AV — DP × Casing" value={avDPCasing} />
            </Col>
            <Col md={3}>
              <AVResultCard label="AV — DC × Casing" value={avDCCasing} />
            </Col>
          </Row>
          <p className="text-muted fs-10 mt-2 mb-0">
            Formula: AV = (0.32 × GPM) / (Hole ID² − Pipe OD²)
          </p>
        </Card.Body>
      </Card>

      {/* ── Daily Job Log ─────────────────────────────────────────────────────── */}
      <Card className="pjr-section-card green">
        <Card.Header className="py-2 d-flex justify-content-between align-items-center">
          <span className="fw-semibold fs-9 text-uppercase">Daily Job Log</span>
          <Button size="sm" variant="outline-success" onClick={addLogEntry}>
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            Add Day
          </Button>
        </Card.Header>
        <Card.Body>
          {reportData.jobLog.length === 0 ? (
            <div className="text-center py-4 text-muted fs-9">
              No log entries yet. Click "Add Day" to start the daily narrative.
            </div>
          ) : (
            reportData.jobLog.map((entry, idx) => (
              <div
                key={idx}
                className={`mb-3 pb-3 ${
                  idx < reportData.jobLog.length - 1 ? 'border-bottom' : ''
                }`}
              >
                <div className="d-flex gap-2 align-items-start">
                  <div style={{ flexShrink: 0 }}>
                    <Form.Label className="fs-9 fw-semibold mb-1">
                      Date
                    </Form.Label>
                    <Form.Control
                      size="sm"
                      type="date"
                      value={entry.date}
                      onChange={e => updateLog(idx, 'date', e.target.value)}
                      style={{ width: 150 }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Form.Label className="fs-9 fw-semibold mb-1">
                      Narrative
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      size="sm"
                      placeholder="Describe daily operations in detail..."
                      value={entry.narrative}
                      onChange={e =>
                        updateLog(idx, 'narrative', e.target.value)
                      }
                      style={{
                        resize: 'vertical',
                        fontFamily: 'inherit',
                        textTransform: 'uppercase'
                      }}
                    />
                  </div>
                  <div style={{ paddingTop: '1.4rem' }}>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => removeLogEntry(idx)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default Step5EngineeringLog;
